import { Box, Fade, Skeleton } from '@mui/material';
import * as MuiButton from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { styled } from '@mui/material/styles';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useContext, useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';

import type { SearchCampSiteQueryVariables } from '@/__generated__/public-graphql';
import {
  Order_By,
  useAddCampsiteSearchLogByPublicMutation,
} from '@/__generated__/public-graphql';
import { useAddCampsiteSearchLogByUserMutation } from '@/__generated__/user-graphql';
import SearchCondition from '@/contents/CampSite/search/SearchCondition';
import Button from '@/contents/General/Button';
import Icon from '@/contents/General/Icon';
import { SearchNotEnteredSkelton } from '@/contents/General/Skeleton';
import Stack from '@/contents/General/Stack';
import Typography from '@/contents/General/Typography';
import { filterCheckedList } from '@/contents/hooks/searchHooks';
import { GlobalContext } from '@/contents/hooks/state';
import { searchEvent } from '@/lib/gtm';
import CampSiteCard from '@/modules/CampSite/CampSiteCard';
import useSearchCampSite from '@/modules/CampSite/public/useSearchCampSite';
import useGetLoginUser from '@/modules/User/user/useGetLoginUser';
import theme from '@/styles/theme';
import { valueToName } from '@/utils/ConstSearchUtil';

const FilterButton = styled(MuiButton.default)`
  justify-content: flex-start;
  padding: 0 ${theme.spacing(3)};
  min-width: 178px;
  height: 42px;
  color: ${theme.palette.primary.main};
  font-size: 14px;
  font-weight: bold;
  border-radius: 22px;
  background: #e2edf5;
`;

const checkedListToFilter = (checkedList: string[]) => {
  const result: SearchCampSiteQueryVariables['where'] = {};

  checkedList.forEach((item) => {
    if (item.match(/^area/) !== null) {
      return;
    }
    if (item.match(/^[0-9]{1,2}/) !== null) {
      const codeResult = item.match(/^([1-9]|[1-3][0-9]|4[0-7])$/);
      if (codeResult === null) {
        throw new Error('invalid code');
      }
      const code = `_${item}`;
      if (!code) {
        throw new Error('invalid code');
      }
      if (!result.prefectures_code) {
        result.prefectures_code = {
          _in: [],
        };
        // eslint-disable-next-line no-underscore-dangle
      }
      // eslint-disable-next-line no-underscore-dangle
      result.prefectures_code._in!.push(code);
    } else {
      // @ts-ignore
      result[item] = {
        _eq: true,
      };
    }
  });
  return result;
};
const snakeToCamel = (str: string) => {
  const result = str.replace(/([-_][a-z])/g, (group) =>
    group.toUpperCase().replace('-', '').replace('_', '')
  );
  if (!result || result === '') {
    throw new Error('invalid code');
  }
  return result;
};
const camelToSnake = (str: string) => {
  const result = str.replace(/([A-Z])/g, (group) => `_${group.toLowerCase()}`);
  if (!result) {
    throw new Error('invalid code');
  }
  return result;
};

const filterToUrlParams = (
  filter: NonNullable<SearchCampSiteQueryVariables['where']>
) => {
  const result: { [key: string]: string } = {};
  Object.keys(filter).forEach((key) => {
    // @ts-ignore
    const value = filter[key];
    if (key === 'prefectures_code') {
      // @ts-ignore
      result.prefecturesCodes = value._in

        // eslint-disable-next-line no-underscore-dangle
        .map((code: string) => code.replace(/^_/, ''))
        .join(',');
    } else if (key === 'sortFilter') {
      result.sortFilter = value;
    } else {
      // @ts-ignore
      result[key] = value._eq;
    }
  });
  // camelCaseに変換
  Object.keys(result).forEach((key) => {
    const value = result[key];
    delete result[key];
    // @ts-ignore
    result[snakeToCamel(key)] = value;
  });

  return result;
};

const SearchPage = ({
  notEntered: notEnteredInput,
}: {
  notEntered?: boolean;
}) => {
  const router = useRouter();
  const { isLogin } = useGetLoginUser();
  const notEntered = notEnteredInput === true;
  const { setDialogStatus, setSearchTotal, dialogStatus } =
    useContext(GlobalContext);
  const [orderBy, setOrderBy] = useState<
    '新着順' | 'お気に入り数順' | '行った！数順'
  >('新着順');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [filter, setFilter] = useState<SearchCampSiteQueryVariables['where']>(
    {}
  );
  const [sortFilter, setSortFilter] = useState<{
    sortFilter: 'updatedAt' | 'totalLikeNum' | 'totalVisitedNum';
  }>({ sortFilter: 'updatedAt' });
  const { checkedList, setCheckedList, historyScrollValue } =
    useContext(GlobalContext);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const openSearchCondition = () => {
    setDialogStatus({
      open: true,
      type: 'COMMON_THIRD',
      props: {
        title: 'エリア・条件から絞り込む',
      },
      children: <SearchCondition />,
    });
  };

  const renderFlgRef = useRef(false);
  const {
    campSites,
    executeQuery,
    total,
    isLoading: loading,
  } = useSearchCampSite({});

  const [isLoading, setIsLoading] = useState(false);

  const fetchCampSites = (
    inputOrderBy?: 'edited_at' | 'total_like_num' | 'total_visited_num',
    limit?: number
  ) => {
    const queryFilter: any = {
      ...filter,
    };
    // @ts-ignore
    const lateCheckout = filter?.late_check_out ?? false;
    // @ts-ignore
    const earlyCheckIn = filter?.early_check_in ?? false;
    if (lateCheckout) {
      queryFilter.checkout_end_hour = {
        _in: ['--', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22'],
      };
      delete queryFilter.late_check_out;
    }
    if (earlyCheckIn) {
      queryFilter.checkin_start_hour = {
        _in: ['--', '5', '05', '06', '6', '07', '7', '08', '8', '09', '9'],
      };
      delete queryFilter.early_check_in;
    }
    executeQuery({
      limit: limit || 16,
      // eslint-disable-next-line no-nested-ternary
      order_by: inputOrderBy
        ? {
            [inputOrderBy]: Order_By.Desc,
          }
        : // eslint-disable-next-line no-nested-ternary
        orderBy === '新着順'
        ? {
            edited_at: Order_By.Desc,
          }
        : orderBy === 'お気に入り数順'
        ? {
            total_like_num: Order_By.Desc,
          }
        : {
            total_visited_num: Order_By.Desc,
          },
      where: {
        ...queryFilter,
        ...(notEntered ? { main_image_id: { _is_null: true } } : {}),
      },
    });
    if (isLogin) {
      useAddCampsiteSearchLogByUserMutation.fetcher({
        filter: {
          limit: limit || 16,
          ...filter,
          ...(notEntered ? { main_image_id: { _is_null: true } } : {}),
        },
        order_by: sortFilter.sortFilter,
      })();
    } else {
      useAddCampsiteSearchLogByPublicMutation.fetcher({
        filter: {
          limit: limit || 16,
          ...filter,
          ...(notEntered ? { main_image_id: { _is_null: true } } : {}),
        },
        order_by: sortFilter.sortFilter,
      })();
    }
  };

  const handleChange = (
    value: '新着順' | 'お気に入り数順' | '行った！数順'
  ) => {
    setAnchorEl(null);
    setOrderBy(value);
    if (value === 'お気に入り数順') {
      setSortFilter({ sortFilter: 'totalLikeNum' });
      fetchCampSites('total_like_num');
    } else if (value === '行った！数順') {
      setSortFilter({ sortFilter: 'totalVisitedNum' });
      fetchCampSites('total_visited_num');
    } else {
      setSortFilter({ sortFilter: 'updatedAt' });
      fetchCampSites('edited_at');
    }
  };
  useEffect(() => {
    if (dialogStatus.open === false && dialogStatus.type === 'COMMON_THIRD') {
      searchEvent('click_search_button', orderBy, filter);
    }
  }, [dialogStatus]);

  useEffect(() => {
    // @ts-ignore
    window.campsite_search_query = JSON.stringify(filter);
    if (dialogStatus.open) {
      searchEvent('calc_search_result_count', orderBy, filter);
    }
  }, [filter]);

  useEffect(() => {
    setFilter(() => {
      return checkedListToFilter(checkedList);
    });
  }, [checkedList]);

  useEffect(() => {
    if (router.isReady === false) {
      return;
    }
    if (!renderFlgRef.current) {
      setTimeout(() => {
        renderFlgRef.current = true;
        // もし、クエリパラメータに値があれば使う
        const { query } = router;
        if (!query || Object.keys(query).length === 0) {
          fetchCampSites(
            undefined,
            historyScrollValue
              ? Math.floor(historyScrollValue[0].value / 100) + 16
              : 16
          );
          searchEvent('initial_search', orderBy, filter);
          return;
        }
        setCheckedList(() => {
          let result: string[] = [];
          let sortFilterQuery = '';
          if (query.prefecturesCodes) {
            const prefecturesCodes = query.prefecturesCodes as string;

            result = Array.from(
              new Set(
                prefecturesCodes
                  .split(',')
                  .map((code) => `${code.replace(/^_/, '')}`)
              )
            );
            delete query.prefecturesCodes;
          }
          if (query.sortFilter) {
            sortFilterQuery = query.sortFilter as string;
            if (sortFilterQuery === 'totalLikeNum') {
              setOrderBy('お気に入り数順');
              setSortFilter({ sortFilter: 'totalLikeNum' });
            } else if (sortFilterQuery === 'totalVisitedNum') {
              setOrderBy('行った！数順');
              setSortFilter({ sortFilter: 'totalVisitedNum' });
            } else {
              setOrderBy('新着順');
              setSortFilter({ sortFilter: 'updatedAt' });
            }
            delete query.sortFilter;
          }
          // eslint-disable-next-line no-restricted-syntax, guard-for-in
          for (const key in query) {
            const value = query[key];
            if (value === 'true') {
              result.push(camelToSnake(key));
            }
          }
          fetchCampSites(
            // checkedListToFilter(result),
            undefined,
            historyScrollValue
              ? Math.floor(historyScrollValue[0].value / 100) + 16
              : 16
          );
          searchEvent('initial_search', orderBy, filter);
          return result;
        });
      }, 100);
    }
  }, [router]);

  useEffect(() => {
    if (!renderFlgRef.current) {
      return;
    }
    if (filter) {
      fetchCampSites();
      router.push(
        // @ts-ignore
        `${router.pathname}/?${new URLSearchParams({
          ...filterToUrlParams(filter),
          ...sortFilter,
        }).toString()}`
      );
    }
  }, [filter]);

  useEffect(() => {
    if (!renderFlgRef.current) {
      return;
    }
    if (orderBy) {
      router.push(
        // @ts-ignore
        `${router.pathname}/?${new URLSearchParams({
          ...sortFilter,
          ...(filter ? filterToUrlParams(filter) : {}),
        }).toString()}`
      );
    }
  }, [orderBy]);

  useEffect(() => {
    setSearchTotal(total);
  }, [total]);

  return (
    <>
      <InfiniteScroll
        pageStart={0}
        loadMore={(page: number) => {
          // setIsLoading(true);
          fetchCampSites(undefined, page * 16);
          // setTimeout(() => setIsLoading(false), 1000);
        }}
        hasMore={total > 0 && campSites.length < total && !isLoading}
        loader={
          <Box key={0}>
            {notEntered ? (
              <Stack
                column
                space={4}
                className="loader"
                key={0}
                padding={`0 ${theme.spacing(3)}`}
              >
                <SearchNotEnteredSkelton />
                <SearchNotEnteredSkelton />
              </Stack>
            ) : (
              <Stack
                column
                space={6}
                className="loader"
                key={0}
                padding={`0 ${theme.spacing(3)}`}
              >
                <Stack column space={1}>
                  <Skeleton
                    variant="rounded"
                    width="100%"
                    height={`calc((100vw - 48px) * 0.475)`}
                    sx={{ borderRadius: 2, maxHeight: 170 }}
                  />
                </Stack>
                <Stack column space={1}>
                  <Skeleton
                    variant="rounded"
                    width="100%"
                    height={`calc((100vw - 48px) * 0.475)`}
                    sx={{ borderRadius: 2, maxHeight: 170 }}
                  />
                </Stack>
              </Stack>
            )}
          </Box>
        }
        useWindow={true}
      >
        <Box sx={{ paddingBottom: theme.spacing(6) }}>
          {notEntered && (
            <>
              <Box sx={{ padding: `${theme.spacing(5)} ${theme.spacing(3)}` }}>
                <Typography
                  fontSize={28}
                  fontWeight={700}
                  sx={{ paddingBottom: theme.spacing(2) }}
                  variant="h1"
                >
                  情報募集中のキャンプ場
                </Typography>
                <Typography
                  fontSize={15}
                  sx={{ paddingBottom: theme.spacing(3) }}
                >
                  iihiには、ユーザーのみなさまのおかげで数多くのキャンプ場情報が集まっていますが、情報が限られていたり写真がなく施設のイメージがつきにくいキャンプ場もまだまだあります。
                  <br />
                  キャンプに行ったら、ぜひiihiに記入・編集、写真を登録いただき、みんなでキャンプ情報を共有、新たなキャンプ場を開拓していきませんか？
                </Typography>
                <Stack alignCenter>
                  <Icon iconType="arrow-right" color="primary" />
                  <Link
                    href={`/contact/user/?category=キャンプ場の新規登録依頼`}
                  >
                    <a>
                      <Typography
                        fontSize={14}
                        color="primary"
                        fontWeight={700}
                      >
                        キャンプ場の新規登録はこちら
                      </Typography>
                    </a>
                  </Link>
                </Stack>
              </Box>
            </>
          )}
          {!notEntered && (
            <Box sx={{ padding: `${theme.spacing(5)} ${theme.spacing(3)}` }}>
              <Typography fontSize={28} fontWeight={700} variant="h1">
                条件から探す
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              padding: `0 ${theme.spacing(3)}`,
              paddingBottom: theme.spacing(5),
            }}
          >
            <Button
              component="label"
              onClick={openSearchCondition}
              fullWidth
              sx={{
                padding: 2,
                border: 'solid 1px #e3e9e6',
                borderRadius: '8px',
                background: '#ffffff',
              }}
            >
              <Stack alignCenter between sx={{ width: '100%' }}>
                <Box sx={{ maxWidth: 'calc(100% - 28px)' }}>
                  <Stack
                    alignCenter
                    space={2}
                    sx={{ paddingBottom: theme.spacing(1) }}
                  >
                    <Typography
                      fontSize={16}
                      fontWeight={700}
                      color="primary"
                      sx={{ minWidth: '54px', whiteSpace: 'nowrap' }}
                    >
                      エリア
                    </Typography>
                    {filterCheckedList('area', checkedList).length === 0 ? (
                      <Typography
                        fontSize={14}
                        fontWeight={500}
                        color="#C2C8C4"
                      >
                        未選択
                      </Typography>
                    ) : (
                      <Typography
                        fontSize={14}
                        sx={{
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          overflowX: 'hidden',
                        }}
                      >
                        {filterCheckedList('area', checkedList).map(
                          (item, i) => {
                            return (
                              <span key={item} style={{ paddingRight: 1 }}>
                                {valueToName(item)}{' '}
                                {i < checkedList.length - 1 && ','}
                              </span>
                            );
                          }
                        )}
                      </Typography>
                    )}
                  </Stack>
                  <Stack alignCenter space={2}>
                    <Typography
                      fontSize={16}
                      fontWeight={700}
                      color="primary"
                      sx={{ minWidth: '54px', whiteSpace: 'nowrap' }}
                    >
                      条件
                    </Typography>
                    {filterCheckedList('condition', checkedList).length ===
                    0 ? (
                      <Typography
                        fontSize={14}
                        fontWeight={500}
                        color="#C2C8C4"
                      >
                        未選択
                      </Typography>
                    ) : (
                      <Typography
                        fontSize={14}
                        sx={{
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          overflowX: 'hidden',
                        }}
                      >
                        {filterCheckedList('condition', checkedList).map(
                          (item, i) => {
                            return (
                              <span key={item} style={{ paddingRight: 1 }}>
                                {valueToName(item)}{' '}
                                {i < checkedList.length - 1 && ','}
                              </span>
                            );
                          }
                        )}
                      </Typography>
                    )}
                  </Stack>
                </Box>
                <Box>
                  <IconButton>
                    <Icon iconType="add" />
                  </IconButton>
                </Box>
              </Stack>
            </Button>
          </Box>
          <Stack
            alignCenter
            between
            sx={{
              padding: `0 ${theme.spacing(3)}`,
              paddingBottom: theme.spacing(3),
            }}
          >
            <Stack alignEnd>
              <Typography fontSize={21} fontWeight={900}>
                {total <= 100 ? total : 100}
              </Typography>
              <Typography
                fontSize={12}
                sx={{ paddingLeft: '4px', paddingBottom: '4px' }}
              >
                件{total > 100 && '以上'}
              </Typography>
            </Stack>
            <FilterButton
              startIcon={<Icon iconType="filter" />}
              onClick={handleClick}
            >
              {orderBy}
            </FilterButton>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={() => {
                setAnchorEl(null);
              }}
              TransitionComponent={Fade}
              onChange={(e) => {
                fetchCampSites();
              }}
            >
              <MenuItem onClick={() => handleChange('新着順')}>新着順</MenuItem>
              <MenuItem onClick={() => handleChange('お気に入り数順')}>
                お気に入り数順
              </MenuItem>
              <MenuItem onClick={() => handleChange('行った！数順')}>
                行った！数順
              </MenuItem>
            </Menu>
          </Stack>
          <Stack
            column
            space={4}
            sx={{
              padding: `0 ${theme.spacing(3)}`,
            }}
          >
            {campSites.map((campSite) => {
              return (
                <CampSiteCard
                  cardSize="wide"
                  variant={notEntered ? 'flex' : 'inner-title'}
                  key={campSite.id}
                  campSite={campSite}
                />
              );
            })}
          </Stack>
        </Box>
      </InfiniteScroll>
    </>
  );
};

export default SearchPage;
